import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { getShopifyProducts } from '../../../../../services/flaskService';
import SelectionCard from './SelectionCard';
import { ShopifyProduct } from '../../../../../types/shopify';
import { SHOPIFY_DATA_UNAVAILABLE_MESSAGE } from '../../../../../utils/shopifyUtils';
import { ff } from '../../../../../theme';

interface ShopifyProductSelectionProps {
  clientId: string;
  availableVariants: Record<string, string[]>;
  selectedVariants: Record<string, string>;
  onVariantSelection: (productId: string, variantId: string) => void;
}

const ShopifyProductSelection: React.FC<ShopifyProductSelectionProps> = ({
  clientId,
  availableVariants,
  selectedVariants = {},
  onVariantSelection,
}) => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hasMultipleVariants = products.some(product => 
    (availableVariants[product.id] || []).length > 1
  ) 

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const productIds = Object.keys(availableVariants || {});
        const fetchedProducts = await getShopifyProducts(clientId, productIds);
        setProducts(fetchedProducts);
        setError(null);
      } catch (err) {
        setError(SHOPIFY_DATA_UNAVAILABLE_MESSAGE);
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    if (Object.keys(availableVariants || {}).length > 0) {
      fetchProducts();
    } else {
      setLoading(false);
    }
  }, [clientId, availableVariants]);

  if (loading) {
    return (
      <Box p={4} textAlign="center">
        <Spinner size="lg" color={ff.ink} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} textAlign="center" color={ff.text2} fontSize="14px">
        {error}
      </Box>
    );
  }

  if (products.length === 0) {
    return (
      <Box p={4} textAlign="center" color={ff.text2} fontSize="14px">
        No complimentary products are available for selection at this time
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <Box>
        <Text textStyle="display" fontSize="20px" color={ff.ink}>
          Confirm products
        </Text>
        <Text fontSize="14px" color={ff.text2} mt={1}>
        View your complimentary products {hasMultipleVariants && "and select your preferred options"}
        </Text>
      </Box>
      {products.map((product) => (
        <SelectionCard
          key={product.id}
          product={product}
          availableVariants={availableVariants[product.id] || []}
          selectedVariant={selectedVariants[product.id] || null}
          onVariantSelect={(variantId) => 
            onVariantSelection(product.id, variantId)
          }
        />
      ))}
    </VStack>
  );
};

export default ShopifyProductSelection; 